import PropTypes from "prop-types";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import { toast } from "react-toastify";
import { Alert, Box, Card, CardContent, Dialog, DialogActions, DialogContent, DialogTitle, Link, Stack, Typography } from "@mui/material";
import ActionButton from "src/components/shared/ActionButton";
import { casesApi } from "./api";
import { dateTime, money, statusLabel } from "./format";

// "Travelling together" on a case (API docs/travelling-together.md, migration
// 033): who invited this customer, whom they invited, what came off Safrati's
// fee, and the refunds to make. The bank's gateway can't refund, so an
// inviter who had already paid gets their 10% back from staff in the bank's
// portal; "Refund made" records who did it and when. Nothing shows before
// 033 (together is null) or when there is nothing to say.

const KIND_WORDS = {
  together: "2 or more travellers",
  invited: "came through an invite",
  inviter: "someone they invited paid",
};

function RefundDone({ refund, onChanged }) {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    setBusy(true);
    try {
      await casesApi.refundDone(refund.id, dispatch);
      toast.success(`Refund of ${money(refund.amount)} recorded`);
      setOpen(false);
      onChanged?.();
    } catch {
      toast.error("Couldn’t record the refund. Try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <ActionButton variant="contained" size="small" onClick={() => setOpen(true)} sx={{ mt: 1 }}>
        Refund made
      </ActionButton>
      {open && (
        <Dialog open onClose={() => !busy && setOpen(false)} maxWidth="xs" fullWidth aria-labelledby="refund-done-title">
          <DialogTitle id="refund-done-title">Refund made in the bank’s portal?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              Only once {money(refund.amount)} has been refunded to {refund.to || "the customer"} in the bank’s portal. Your name and the time are kept on the case.
            </Typography>
          </DialogContent>
          <DialogActions>
            <ActionButton onClick={() => setOpen(false)} disabled={busy}>
              Not yet
            </ActionButton>
            <ActionButton variant="contained" onClick={confirm} busy={busy}>
              Yes, it’s refunded
            </ActionButton>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}

RefundDone.propTypes = { refund: PropTypes.object.isRequired, onChanged: PropTypes.func };

export default function Together({ caseData, canEdit, onChanged }) {
  const together = caseData?.together;
  if (!together) return null;
  const { invited_by: invitedBy, invited = [], discounts = [], refunds = [] } = together;
  const live = discounts.filter((d) => d.status === "applied" || d.status === "pending");
  if (!invitedBy && !invited.length && !live.length && !refunds.length) return null;
  const caseLink = (id) => (
    <Link component={RouterLink} to={`/cases/${id}`} sx={{ fontWeight: 600 }}>
      application {id}
    </Link>
  );
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" component="h2" mb={1}>
          Travelling together
        </Typography>
        <Stack spacing={1.25}>
          {refunds
            .filter((r) => r.status === "refund_due")
            .map((r) => (
              <Alert key={r.id} severity="warning" icon={false}>
                <Typography variant="body2" fontWeight={700}>
                  Refund {money(r.amount)} to {r.to || "the customer"}: {r.invitee || "someone they invited"} has paid ({caseLink(r.from_application)}).
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  10% of Safrati’s fee, on the card they paid with. Refund it in the bank’s portal, then record it here.
                </Typography>
                {canEdit && <RefundDone refund={r} onChanged={onChanged} />}
              </Alert>
            ))}
          {refunds
            .filter((r) => r.status === "refunded")
            .map((r) => (
              <Typography key={r.id} variant="body2" color="text.secondary">
                Refunded {money(r.amount)} for inviting {r.invitee || "someone"}
                {r.done_at ? ` · ${dateTime(r.done_at)}` : ""}
              </Typography>
            ))}
          {invitedBy && (
            <Typography variant="body2">
              Invited by <b>{invitedBy.name || "a customer"}</b>
              {invitedBy.application_id ? <> ({caseLink(invitedBy.application_id)})</> : null}
            </Typography>
          )}
          {invited.length > 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Invited through this application:
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {invited.map((p) => (
                  <Typography component="li" variant="body2" key={p.application_id}>
                    {p.name || "A customer"} · {caseLink(p.application_id)} · {p.paid ? "paid" : statusLabel(p.status)}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
          {live.map((d) => (
            <Typography key={d.id} variant="body2">
              {d.status === "pending" ? "Due at their checkout" : "Taken off"}: <b>−{money(d.amount)}</b> of Safrati’s fee ({KIND_WORDS[d.kind] || d.kind}
              {caseData.travellers.length > 1 ? `, traveller ${caseData.travellers.findIndex((t) => t.request_id === d.request_id) + 1}` : ""})
            </Typography>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}

Together.propTypes = { caseData: PropTypes.object.isRequired, canEdit: PropTypes.bool, onChanged: PropTypes.func };

// Where the customer spent time while applying (API docs/apply-insights.md):
// steps by time, the slowest questions, where they paused long or met
// repeated errors. Only ids, counts and seconds were kept: no answers.
const minutes = (s) => (s < 60 ? "under a minute" : `${Math.round(s / 60)} min`);

export function Insights({ caseData }) {
  const insights = caseData?.insights;
  if (!insights) return null;
  const slow = (insights.questions || []).filter((q) => q.seconds >= 30 || q.errors > 0).slice(0, 5);
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" component="h2" mb={0.5}>
          Where they spent time
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={1}>
          {/* Every page of the application, after submitting too: the case's
              header counts only the active time on the form (georges-28). */}
          {minutes(insights.total_seconds)} in all, on every page of the application
          {insights.last_step ? ` · last seen on ${insights.last_step}` : ""}
          {insights.expiries ? ` · signed out ${insights.expiries}×` : ""}
        </Typography>
        <Stack spacing={1}>
          {(insights.stuck || []).length > 0 && (
            <Alert severity="info" icon={false}>
              <Typography variant="body2" fontWeight={700}>
                Stuck on:
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {insights.stuck.slice(-4).map((s) => (
                  <Typography component="li" variant="body2" key={`${s.question_id}-${s.reason}`}>
                    {s.label} ({s.reason === "pause" ? "a long pause" : "repeated errors"}
                    {s.step_name ? `, ${s.step_name}` : ""})
                  </Typography>
                ))}
              </Box>
            </Alert>
          )}
          <Box component="dl" sx={{ m: 0, display: "grid", gridTemplateColumns: "1fr auto", columnGap: 2, rowGap: 0.25 }}>
            {(insights.steps || []).slice(0, 6).map((s) => (
              <Box key={s.key} sx={{ display: "contents" }}>
                <Typography component="dt" variant="body2" color="text.secondary">
                  {s.name}
                  {s.visits > 1 ? ` · opened ${s.visits}×` : ""}
                </Typography>
                <Typography component="dd" variant="body2" sx={{ m: 0, fontWeight: 600 }}>
                  {minutes(s.seconds)}
                </Typography>
              </Box>
            ))}
          </Box>
          {slow.length > 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Slowest questions:
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {slow.map((q) => (
                  <Typography component="li" variant="body2" key={q.question_id}>
                    {q.label}: {minutes(q.seconds)}
                    {q.errors ? ` · ${q.errors} error${q.errors === 1 ? "" : "s"}` : ""}
                    {q.ask ? " · asked Ask Safrati" : ""}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

Insights.propTypes = { caseData: PropTypes.object.isRequired };
